/**
 * Oculus / Meta viseme weights → canonical ARKit lip-sync weights.
 *
 * `@met4citizen/headaudio` emits 15 Oculus visemes (`viseme_aa`, `viseme_PP`,
 * `viseme_E`, …) by analysing audio in real time. None of those names exist
 * as morph targets on most character rigs — the rigs ship with ARKit shapes
 * (`jawOpen`, `mouthFunnel`, …) instead.
 *
 * This module converts the live viseme alpha vector into a canonical ARKit
 * weight dict so the figure's lip-sync layer can be rig-agnostic. The ARKit
 * dict is then either:
 *
 *   • routed straight through to mesh morphs whose name matches the canonical
 *     (Avaturn / RPM), or
 *   • fanned out to CC V_* viseme morphs by `cc-face-rig.expandCcBaseVisemeWeights`.
 *
 * Weights are tuned for HeadAudio's default `visemeMaxs ≈ 0.65`. Effective
 * `jawOpen` for an "aa" peaks at `0.65 × 0.9 ≈ 0.59`, which reads as a clear
 * open mouth on Avaturn / Lewis. CC characters need the additional `Jaw_Open`
 * passthrough in the CC fan-out to actually visibly open at this amplitude
 * (the `V_Open` / `V_Lip_Open` deltas alone are ~0.8 cm; `Jaw_Open` is ~2 cm).
 *
 * The vocabulary is closed (15 entries) and identical to HeadAudio's output
 * keys. Any unknown key in the input is silently dropped.
 */

import type { ArkitBlendshape } from './morph-aliases';

/** HeadAudio viseme keys (Oculus / Meta vocabulary). */
export const HEADAUDIO_VISEME_NAMES = [
  'viseme_aa', 'viseme_E', 'viseme_I', 'viseme_O', 'viseme_U',
  'viseme_PP', 'viseme_SS', 'viseme_TH', 'viseme_DD', 'viseme_FF',
  'viseme_kk', 'viseme_nn', 'viseme_RR', 'viseme_CH', 'viseme_sil',
] as const;

export type HeadAudioViseme = (typeof HEADAUDIO_VISEME_NAMES)[number];

type VisemeWeights = Partial<Record<ArkitBlendshape, number>>;

/**
 * Per-viseme ARKit contribution at full activation. Multiplied by the live
 * HeadAudio alpha (0..~0.65) at runtime.
 *
 * Tuning notes:
 *   - `viseme_aa` is the workhorse "open vowel" — 0.9 jawOpen so it reads.
 *   - `viseme_PP` is closed-lip; relies on mouthClose + press, no jaw.
 *   - `viseme_FF` adds an upper-lip lift (teeth on lower lip).
 *   - `viseme_O` / `viseme_U` use mouthFunnel + mouthPucker for rounding.
 */
export const VISEME_TO_ARKIT: Record<HeadAudioViseme, VisemeWeights> = {
  viseme_sil: {},
  viseme_PP: {
    mouthClose: 0.85,
    mouthPressLeft: 0.45,
    mouthPressRight: 0.45,
  },
  viseme_FF: {
    jawOpen: 0.15,
    mouthFunnel: 0.35,
    mouthUpperUpLeft: 0.55,
    mouthUpperUpRight: 0.55,
  },
  viseme_TH: {
    jawOpen: 0.35,
    tongueOut: 0.25,
    mouthFunnel: 0.2,
  },
  viseme_DD: {
    jawOpen: 0.45,
    mouthStretchLeft: 0.15,
    mouthStretchRight: 0.15,
  },
  viseme_kk: {
    jawOpen: 0.55,
    mouthFunnel: 0.15,
  },
  viseme_CH: {
    jawOpen: 0.4,
    mouthFunnel: 0.55,
    mouthPucker: 0.25,
  },
  viseme_SS: {
    jawOpen: 0.12,
    mouthSmileLeft: 0.35,
    mouthSmileRight: 0.35,
    mouthStretchLeft: 0.2,
    mouthStretchRight: 0.2,
  },
  viseme_nn: {
    jawOpen: 0.25,
    mouthClose: 0.15,
  },
  viseme_RR: {
    jawOpen: 0.3,
    mouthFunnel: 0.45,
    mouthRollLower: 0.2,
  },
  viseme_aa: {
    jawOpen: 0.9,
    mouthFunnel: 0.15,
  },
  viseme_E: {
    jawOpen: 0.45,
    mouthSmileLeft: 0.55,
    mouthSmileRight: 0.55,
  },
  viseme_I: {
    jawOpen: 0.2,
    mouthSmileLeft: 0.85,
    mouthSmileRight: 0.85,
    mouthStretchLeft: 0.25,
    mouthStretchRight: 0.25,
  },
  viseme_O: {
    jawOpen: 0.55,
    mouthFunnel: 0.65,
    mouthPucker: 0.45,
  },
  viseme_U: {
    jawOpen: 0.25,
    mouthPucker: 0.9,
    mouthFunnel: 0.75,
  },
};

/**
 * Merge the active viseme alpha vector into canonical ARKit lip-sync weights.
 * Per-shape contributions are max-merged (loudest viseme wins), not summed —
 * summing would push values past 1.0 when overlapping visemes spike at once.
 *
 * Visemes with `alpha <= 0.001` are skipped entirely (HeadAudio's silence
 * floor) so an idle audio stream produces an empty weight dict.
 */
export function visemeAlphasToArkitWeights(
  visemeAlphas: Readonly<Record<string, number>>,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [visemeKey, alpha] of Object.entries(visemeAlphas)) {
    if (alpha <= 0.001) continue;
    const map = VISEME_TO_ARKIT[visemeKey as HeadAudioViseme];
    if (!map) continue;
    for (const [shape, weight] of Object.entries(map)) {
      const v = alpha * weight;
      out[shape] = Math.max(out[shape] ?? 0, v);
    }
  }
  return out;
}
